import { LitElement, html, TemplateResult } from "lit";
import { customElement, property } from "lit/decorators.js";
import { unsafeHTML } from "lit/directives/unsafe-html.js";
import * as Diff from "diff";
import type {
	MessageParam,
	TextBlockParam,
	ContentBlock,
	ContentBlockParam,
	Message,
	ToolUnion,
} from "@anthropic-ai/sdk/resources/messages";
import { SimpleConversation, EnhancedMessageParam } from "../utils/simple-conversation-processor";
import { markdownToHtml } from "../utils/markdown";

@customElement("simple-conversation-view")
export class SimpleConversationView extends LitElement {
	@property({ type: Array }) conversations: SimpleConversation[] = [];

	// Disable shadow DOM to use global CSS
	createRenderRoot() {
		return this;
	}

	private toggleContent(e: Event) {
		const header = e.currentTarget as HTMLElement;
		const content = header.nextElementSibling as HTMLElement;
		const toggle = header.querySelector("span:first-child") as HTMLElement;

		if (content && toggle) {
			const isHidden = content.classList.contains("hidden");
			content.classList.toggle("hidden", !isHidden);
			toggle.textContent = isHidden ? "[-]" : "[+]";
		}
	}

	private toggleWriteContent(e: Event) {
		const preview = e.currentTarget as HTMLElement;
		const fullContent = preview.previousElementSibling as HTMLElement;

		if (fullContent) {
			const isExpanded = !fullContent.classList.contains("hidden");
			if (isExpanded) {
				// Collapse: hide full content, show preview
				fullContent.classList.add("hidden");
				preview.classList.remove("hidden");
			} else {
				// Expand: show full content, hide preview
				fullContent.classList.remove("hidden");
				preview.classList.add("hidden");
			}
		}
	}

	private formatContent(content: string | ContentBlockParam[], toolResults?: Record<string, any>): TemplateResult {
		if (typeof content === "string") {
			return this.formatStringContent(content);
		}

		if (Array.isArray(content)) {
			return html`
				${content.map((block) => {
					if (block.type === "text") {
						return this.formatStringContent(block.text);
					} else if (block.type === "tool_result") {
						// Skip standalone tool_result blocks - they will be paired with tool_use
						return html``;
					} else if (block.type === "tool_use") {
						const toolUse = block as any;
						const toolResult = toolResults?.[toolUse.id];

						if (block.name === "TodoWrite" || block.name === "Edit" || block.name === "MultiEdit") {
							return html`
								<div class="mb-4">
									<div class="text-vs-type font-bold  px-4 py-2 inline-block mb-2">
										${this.getToolDisplayName(block)}
									</div>
									<div class="bg-vs-bg-secondary p-4 text-vs-text">${this.renderToolUseContent(block)}</div>
									${toolResult ? this.renderToolResult(toolResult) : ""}
								</div>
							`;
						}
						if (block.name === "Write") {
							return html`
								<div class="mb-4">
									<div class="text-vs-type font-bold  px-4 py-2 inline-block mb-2">
										${this.getToolDisplayName(block)}
									</div>
									<div class="bg-vs-bg-secondary p-4 text-vs-text hidden">
										${this.renderToolUseContent(block)}
									</div>
									<div
										class="bg-vs-bg-secondary p-4 text-vs-text cursor-pointer hover:bg-vs-border transition-colors"
										@click=${this.toggleWriteContent}
									>
										${this.renderWritePreview(block)}
									</div>
									${toolResult ? this.renderToolResult(toolResult) : ""}
								</div>
							`;
						}
						return html`
							<div class="mb-4">
								<div
									class="text-vs-type font-bold  px-4 py-2 inline-block mb-2 cursor-pointer hover:text-white transition-colors"
									@click=${this.toggleContent}
								>
									<span class="mr-2">[+]</span>
									${this.getToolDisplayName(block)}
								</div>
								<div class="bg-vs-bg-secondary p-4 text-vs-text hidden">
									${this.renderToolUseContent(block)}
								</div>
								${toolResult ? this.renderToolResult(toolResult) : ""}
							</div>
						`;
					}
					return html`<pre class="mb-4">${JSON.stringify(block, null, 2)}</pre>`;
				})}
			`;
		}

		return html`<pre>${JSON.stringify(content, null, 2)}</pre>`;
	}

	private formatStringContent(content: string): TemplateResult {
		// Check for system reminder blocks (handling HTML-escaped delimiters)
		const systemReminderRegex = /&lt;system-reminder&gt;([\s\S]*?)&lt;\/system-reminder&gt;/g;
		const systemReminders: string[] = [];
		let match;

		// Extract all system reminder blocks
		while ((match = systemReminderRegex.exec(content)) !== null) {
			systemReminders.push(match[1].trim());
		}

		// Remove system reminder blocks from main content
		const mainContent = content.replace(systemReminderRegex, "").trim();

		return html`
			${mainContent ? html`<div class="markdown-content">${unsafeHTML(markdownToHtml(mainContent))}</div>` : ""}
			${systemReminders.length > 0
				? html`
						<div class="mb-4">
							<div
								class="cursor-pointer text-vs-muted hover:text-white transition-colors"
								@click=${this.toggleContent}
							>
								<span class="mr-2">[+]</span>
								<span>System Reminder</span>
							</div>
							<div class="hidden mt-2 text-vs-muted">
								${systemReminders.map(
									(reminder, index) => html`
										<div>
											${systemReminders.length > 1
												? html`<div class="text-vs-function font-bold mb-2">Reminder ${index + 1}:</div>`
												: ""}
											<div class="markdown-content">${unsafeHTML(markdownToHtml(reminder))}</div>
										</div>
									`,
								)}
							</div>
						</div>
					`
				: ""}
		`;
	}

	private formatSystem(system: string | TextBlockParam[] | undefined): string {
		if (!system) return "";

		if (typeof system === "string") {
			return markdownToHtml(system);
		}

		if (Array.isArray(system)) {
			const textContent = system
				.map((block) => {
					if (block.type === "text") {
						return block.text;
					}
					return JSON.stringify(block, null, 2);
				})
				.join("\n");
			return markdownToHtml(textContent);
		}

		return JSON.stringify(system, null, 2);
	}

	private formatResponseContent(response: Message): TemplateResult {
		if (!response) return html``;

		if (response.content && Array.isArray(response.content)) {
			return html`
				${response.content.map((block) => {
					if (block.type === "text") {
						return html`<div class="markdown-content">${unsafeHTML(markdownToHtml(block.text))}</div>`;
					} else if (block.type === "tool_use") {
						if (block.name === "TodoWrite") {
							return html`
								<div class="mb-4">
									<div class="text-vs-type font-bold  px-4 py-2 inline-block mb-2">
										${this.getToolDisplayName(block)}
									</div>
									<div class="bg-vs-bg-secondary p-4 text-vs-text">${this.renderToolUseContent(block)}</div>
								</div>
							`;
						}
						return html`
							<div class="mb-4">
								<div
									class="text-vs-type font-bold  px-4 py-2 inline-block mb-2 cursor-pointer hover:text-white transition-colors"
									@click=${this.toggleContent}
								>
									<span class="mr-2">[+]</span>
									${this.getToolDisplayName(block)}
								</div>
								<div class="bg-vs-bg-secondary p-4 text-vs-text hidden">
									${this.renderToolUseContent(block)}
								</div>
							</div>
						`;
					}
					return html`<pre class="mb-4">${JSON.stringify(block, null, 2)}</pre>`;
				})}
			`;
		}

		return html`<pre>${JSON.stringify(response, null, 2)}</pre>`;
	}

	private getToolDisplayName(toolUse: any): TemplateResult {
		const toolName = toolUse.name;
		const input = toolUse.input;

		// HTML unescape function
		const unescapeHtml = (str: string): string => {
			const div = document.createElement("div");
			div.innerHTML = str;
			return div.textContent || div.innerText || "";
		};

		switch (toolName) {
			case "Read":
				return input?.file_path
					? html`${toolName}(<span class="text-vs-text">${unescapeHtml(input.file_path)}</span>)`
					: html`${toolName}`;
			case "Bash":
				return input?.command
					? html`${toolName}(<span class="text-vs-text">${unescapeHtml(input.command)}</span>)`
					: html`${toolName}`;
			case "Write":
				return input?.file_path
					? html`${toolName}(<span class="text-vs-text">${unescapeHtml(input.file_path)}</span>)`
					: html`${toolName}`;
			case "Glob":
				if (input?.pattern) {
					const pattern = unescapeHtml(input.pattern);
					const path = input?.path ? unescapeHtml(input.path) : null;
					return path
						? html`${toolName}(<span class="text-vs-text">${pattern}</span>,
								<span class="text-vs-text">${path}</span>)`
						: html`${toolName}(<span class="text-vs-text">${pattern}</span>)`;
				}
				return html`${toolName}`;
			case "Grep":
				if (input?.pattern) {
					const pattern = unescapeHtml(input.pattern);
					const include = input?.include ? unescapeHtml(input.include) : null;
					const path = input?.path ? unescapeHtml(input.path) : null;

					let params = pattern;
					if (include) params += `, ${include}`;
					if (path) params += `, ${path}`;

					return html`${toolName}(<span class="text-vs-text">${params}</span>)`;
				}
				return html`${toolName}`;
			case "LS":
				if (input?.path) {
					const path = unescapeHtml(input.path);
					const ignore = input?.ignore ? input.ignore.map((p: string) => unescapeHtml(p)).join(", ") : null;

					return ignore
						? html`${toolName}(<span class="text-vs-text">${path}</span>, ignore:
								<span class="text-vs-text">${ignore}</span>)`
						: html`${toolName}(<span class="text-vs-text">${path}</span>)`;
				}
				return html`${toolName}`;
			case "Edit":
				return input?.file_path
					? html`${toolName}(<span class="text-vs-text">${unescapeHtml(input.file_path).split("/").pop()}</span>)`
					: html`${toolName}`;
			case "MultiEdit":
				if (input?.file_path) {
					const fileName = unescapeHtml(input.file_path).split("/").pop();
					const editCount = input?.edits ? input.edits.length : 0;
					return html`${toolName}(<span class="text-vs-text">${fileName}</span>,
						<span class="text-vs-text">${editCount} edits</span>)`;
				}
				return html`${toolName}`;
			case "NotebookRead":
				return input?.notebook_path
					? html`${toolName}(<span class="text-vs-text">${unescapeHtml(input.notebook_path).split("/").pop()}</span
							>)`
					: html`${toolName}`;
			case "NotebookEdit":
				if (input?.notebook_path && input?.cell_number !== undefined) {
					const fileName = unescapeHtml(input.notebook_path).split("/").pop();
					const cellNum = input.cell_number;
					const mode = input?.edit_mode || "replace";
					return html`${toolName}(<span class="text-vs-text">${fileName}</span>, cell
						<span class="text-vs-text">${cellNum}</span>, <span class="text-vs-text">${mode}</span>)`;
				}
				return html`${toolName}`;
			default:
				return html`${toolName}`;
		}
	}

	private renderToolUseContent(toolUse: any): TemplateResult {
		const toolName = toolUse.name;
		const input = toolUse.input;

		// HTML unescape function
		const unescapeHtml = (str: string): string => {
			const div = document.createElement("div");
			div.innerHTML = str;
			return div.textContent || div.innerText || "";
		};

		if (toolName === "TodoWrite" && input?.todos) {
			const todos = input.todos;

			return html`
				<div class="overflow-x-auto">
					${todos.map((todo: any) => {
						const statusClass =
							todo.status === "completed"
								? "line-through text-vs-text"
								: todo.status === "in_progress"
									? "text-green-400"
									: "text-vs-muted";

						return html`
							<div class="mb-1 overflow-hidden whitespace-nowrap text-ellipsis ${statusClass}">
								• ${todo.content}
							</div>
						`;
					})}
				</div>
			`;
		}

		if (toolName === "NotebookEdit" && input?.new_source) {
			const content = unescapeHtml(input.new_source);

			return html`
				<div class="overflow-x-auto">
					<pre class="whitespace-pre text-vs-text m-0">${content}</pre>
				</div>
			`;
		}

		if (toolName === "Write" && input?.content) {
			const content = unescapeHtml(input.content);

			return html`
				<div class="overflow-x-auto">
					<pre class="whitespace-pre text-vs-text m-0">${content}</pre>
				</div>
			`;
		}

		if (toolName === "MultiEdit" && input?.edits) {
			const edits = input.edits;

			return html`
				<div class="overflow-x-auto">
					${edits.map((edit: any, index: number) => {
						const oldStr = unescapeHtml(edit.old_string);
						const newStr = unescapeHtml(edit.new_string);

						// Use proper diff algorithm
						const diff = Diff.diffLines(oldStr, newStr);
						const diffLines = [];

						for (const part of diff) {
							const lines = part.value.split("\n");
							// Remove empty last line from split if it exists
							if (lines[lines.length - 1] === "") {
								lines.pop();
							}

							for (const line of lines) {
								if (part.added) {
									diffLines.push(
										html`<div class="bg-green-600/20"><pre class="text-vs-text m-0">+ ${line}</pre></div>`,
									);
								} else if (part.removed) {
									diffLines.push(
										html`<div class="bg-red-600/20"><pre class="text-vs-text m-0">- ${line}</pre></div>`,
									);
								} else {
									diffLines.push(html`<div><pre class="text-vs-text m-0">  ${line}</pre></div>`);
								}
							}
						}

						return html`
							<div class="mb-4">
								<div class="text-vs-muted mb-2">Edit ${index + 1}:</div>
								<div>${diffLines}</div>
							</div>
						`;
					})}
				</div>
			`;
		}

		if (toolName === "Edit" && input?.old_string && input?.new_string) {
			const oldStr = unescapeHtml(input.old_string);
			const newStr = unescapeHtml(input.new_string);

			// Use proper diff algorithm
			const diff = Diff.diffLines(oldStr, newStr);
			const diffLines = [];

			for (const part of diff) {
				const lines = part.value.split("\n");
				// Remove empty last line from split if it exists
				if (lines[lines.length - 1] === "") {
					lines.pop();
				}

				for (const line of lines) {
					if (part.added) {
						diffLines.push(
							html`<div class="bg-green-600/20"><pre class="text-vs-text m-0">+ ${line}</pre></div>`,
						);
					} else if (part.removed) {
						diffLines.push(html`<div class="bg-red-600/20"><pre class="text-vs-text m-0">- ${line}</pre></div>`);
					} else {
						diffLines.push(html`<div><pre class="text-vs-text m-0">  ${line}</pre></div>`);
					}
				}
			}

			return html` <div class="overflow-x-auto">${diffLines}</div> `;
		}

		// Default: show JSON parameters
		return html`
			<div class="overflow-x-auto">
				<pre class="whitespace-pre">${JSON.stringify(input, null, 2)}</pre>
			</div>
		`;
	}

	private renderToolResult(toolResult: any): TemplateResult {
		return html`
			<div class="mb-4">
				<div
					class="text-vs-function font-bold px-4 py-2 inline-block mb-2 cursor-pointer hover:text-white transition-colors"
					@click=${this.toggleContent}
				>
					<span class="mr-2">[+]</span>
					Tool Result ${toolResult.is_error ? "❌" : "✅"}
				</div>
				<div class="bg-vs-bg-secondary p-4 text-vs-text hidden">
					<pre class="whitespace-pre-wrap overflow-x-auto">
${typeof toolResult.content === "string" ? toolResult.content : JSON.stringify(toolResult.content, null, 2)}</pre
					>
				</div>
			</div>
		`;
	}

	private renderWritePreview(toolUse: any): TemplateResult {
		const input = toolUse.input;
		if (!input?.content) {
			return html`<div class="text-vs-muted">No content</div>`;
		}

		// HTML unescape function
		const unescapeHtml = (str: string): string => {
			const div = document.createElement("div");
			div.innerHTML = str;
			return div.textContent || div.innerText || "";
		};

		const content = unescapeHtml(input.content);
		const lines = content.split("\n");
		const preview = lines.slice(0, 10);
		const hasMore = lines.length > 10;

		return html`
			<div class="overflow-x-auto">
				<pre class="whitespace-pre text-vs-text m-0">${preview.join("\n")}</pre>
			</div>
			${hasMore
				? html`<div class="text-vs-muted mt-2 border-t border-vs-border pt-2">
						... ${lines.length - 10} more lines (click to expand)
					</div>`
				: ""}
		`;
	}

	private hasTools(conversation: SimpleConversation): boolean {
		return !!(conversation.finalPair.request.tools && conversation.finalPair.request.tools.length > 0);
	}

	private renderTools(tools: ToolUnion[]): TemplateResult {
		return html`
			${tools.map((tool) => {
				if ("name" in tool && tool.name) {
					const description = ("description" in tool && tool.description) || "No description";

					return html`
						<div class="mb-8">
							<div
								class="cursor-pointer text-vs-user font-bold mb-2 border border-vs-user px-4 py-2 inline-block hover:text-white transition-colors"
								@click=${this.toggleContent}
							>
								<span class="mr-2">[-]</span>
								${tool.name}
							</div>
							<div>
								<div class="text-vs-text mb-3 markdown-content">${unsafeHTML(markdownToHtml(description))}</div>

								${"input_schema" in tool && tool.input_schema && typeof tool.input_schema === "object"
									? (() => {
											const schema = tool.input_schema as any;
											if (schema.properties) {
												return html`
													<div class="text-vs-muted mb-2">Parameters:</div>
													${Object.entries(schema.properties).map(([paramName, paramDef]) => {
														const def = paramDef as any;
														const required = schema.required?.includes(paramName) ? " (required)" : "";
														const type = def.type ? ` [${def.type}]` : "";
														const desc = def.description ? ` - ${def.description}` : "";
														return html`
															<div class="ml-4 mb-1">
																<span class="text-vs-type">${paramName}</span>
																<span class="text-vs-muted">${type}${required}${desc}</span>
															</div>
														`;
													})}
												`;
											}
											return html``;
										})()
									: html``}
							</div>
						</div>
					`;
				}
				return html`<pre class="mb-4">${JSON.stringify(tool, null, 2)}</pre>`;
			})}
		`;
	}

	render() {
		if (this.conversations.length === 0) {
			return html`<div>No conversations found.</div>`;
		}

		return html`
			<div>
				${this.conversations.map(
					(conversation) => html`
						<div class="mt-8 first:mt-0">
							<!-- Conversation Header -->
							<div class="border border-vs-highlight p-4 mb-0">
								<div class="text-vs-assistant font-bold">${Array.from(conversation.models).join(", ")}</div>
								<div class="text-vs-muted">
									${new Date(conversation.metadata.startTime).toLocaleString()} •
									${conversation.messages.length + 1} messages
								</div>
							</div>

							<!-- Compacted Conversation Indicator -->
							${conversation.compacted
								? html`
										<div class="px-4 mt-4">
											<div
												class="cursor-pointer text-red-400 hover:text-red-300 transition-colors"
												@click=${this.toggleContent}
											>
												<span class="mr-2">[+]</span>
												<span>⚠️ Compacted Conversation (${conversation.metadata.totalPairs} API calls merged)</span>
											</div>
											<div class="hidden mt-2 p-3 bg-red-900/20 border border-red-700/50 rounded text-red-200">
												<div class="text-sm">
													This conversation was created by merging multiple API calls that Claude compacted into a single request.
													The original conversation history was preserved by combining separate requests into one unified view.
												</div>
												<div class="text-xs text-red-300 mt-2">
													Total API calls: ${conversation.metadata.totalPairs} •
													Start: ${new Date(conversation.metadata.startTime).toLocaleString()} •
													End: ${new Date(conversation.metadata.endTime).toLocaleString()}
												</div>
											</div>
										</div>
									`
								: ""}

							<!-- System Prompt (Expandable) -->
							${conversation.system
								? html`
										<div class="px-4 mt-4">
											<div
												class="cursor-pointer text-vs-assistant hover:text-white transition-colors"
												@click=${this.toggleContent}
											>
												<span class="mr-2">[+]</span>
												<span>System Prompt</span>
											</div>
											<div class="hidden mt-4">
												<div class="text-vs-text markdown-content">
													${unsafeHTML(this.formatSystem(conversation.system))}
												</div>
											</div>
										</div>
									`
								: ""}

							<!-- Tools (Expandable) -->
							${this.hasTools(conversation)
								? html`
										<div class="px-4">
											<div
												class="cursor-pointer text-vs-assistant hover:text-white transition-colors"
												@click=${this.toggleContent}
											>
												<span class="mr-2">[+]</span>
												<span>Tools (${conversation.finalPair.request.tools?.length || 0})</span>
											</div>
											<div class="mt-4 hidden">
												<div class="text-vs-text">
													${this.renderTools(conversation.finalPair.request.tools || [])}
												</div>
											</div>
										</div>
									`
								: ""}

							<!-- Conversation Messages -->
							<div class="px-4 mt-4">
								${conversation.messages
									.filter((message) => !(message as EnhancedMessageParam).hide)
									.map(
										(message, msgIndex) => html`
											<div class="mb-4">
												<div
													class="font-bold uppercase ${message.role === "user"
														? "text-vs-user"
														: "text-vs-assistant"}"
												>
													<span>${message.role}</span>
												</div>
												<div class="text-vs-text">
													${this.formatContent(
														message.content,
														(message as EnhancedMessageParam).toolResults,
													)}
												</div>
											</div>
										`,
									)}

								<!-- Assistant Response -->
								<div class="mb-4">
									<div class="font-bold uppercase text-vs-assistant">
										<span>assistant</span>
										<span class="font-normal lowercase text-vs-muted ml-2">
											(${conversation.metadata.inputTokens} in, ${conversation.metadata.outputTokens} out)
										</span>
									</div>
									<div class="text-vs-text">${this.formatResponseContent(conversation.response)}</div>
								</div>
							</div>
						</div>
					`,
				)}
			</div>
		`;
	}
}
